import { useEffect, useState } from "react";
import { Bot, RefreshCw, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const empty = {
  enabled: false,
  ask_command_enabled: true,
  mention_response_enabled: true,
  command_trigger: "ask",
  display_name: "TheBarracksBunny",
  personality_prompt: "",
  memory: "",
  style_rules: "",
  max_response_chars: 360,
  cooldown_seconds: 15,
};

const inputClass = "w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm outline-none focus:border-[hsl(var(--primary))]";

export default function AI() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai/personality");
      setForm({ ...empty, ...(data || {}) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/ai/personality", form);
      setForm({ ...empty, ...(data || {}) });
      toast.success("AI personality saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-personality-page">
      <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">AI Chatbot</div>
          <h1 className="text-3xl font-semibold tracking-normal mt-1 flex items-center gap-2">
            <Bot className="w-7 h-7 text-[hsl(var(--primary))]" /> Personality And Memory
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
            Configure TheBarracksBunny's chat personality for !ask, direct mentions, and AI prompt commands. RPG systems do not use this personality.
          </p>
        </div>
        <button type="button" onClick={load} className="h-10 px-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] text-sm inline-flex items-center gap-2 self-start xl:self-auto">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ToggleCard label="Personality enabled" checked={form.enabled} onChange={(enabled) => setForm({ ...form, enabled })} />
        <ToggleCard label={`!${form.command_trigger || "ask"} enabled`} checked={form.ask_command_enabled} onChange={(ask_command_enabled) => setForm({ ...form, ask_command_enabled })} />
        <ToggleCard label="@mention replies" checked={form.mention_response_enabled} onChange={(mention_response_enabled) => setForm({ ...form, mention_response_enabled })} />
      </section>

      <section className="surface p-5 space-y-5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
          <div>
            <div className="label-eyebrow">Behavior</div>
            <h2 className="text-lg font-semibold mt-1">Chat Personality</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Display name">
            <input className={inputClass} value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </Field>
          <Field label="Ask trigger">
            <div className="flex">
              <span className="px-3 py-2 bg-[hsl(var(--background))] border border-r-0 border-[hsl(var(--border))] rounded-l text-sm">!</span>
              <input className={`${inputClass} rounded-l-none`} value={form.command_trigger || "ask"} onChange={(e) => setForm({ ...form, command_trigger: e.target.value })} />
            </div>
          </Field>
          <Field label="Max reply characters">
            <input type="number" className={inputClass} value={form.max_response_chars || 360} onChange={(e) => setForm({ ...form, max_response_chars: Number(e.target.value) })} />
          </Field>
          <Field label="Cooldown seconds">
            <input type="number" className={inputClass} value={form.cooldown_seconds || 15} onChange={(e) => setForm({ ...form, cooldown_seconds: Number(e.target.value) })} />
          </Field>
        </div>

        <Field label="Personality prompt">
          <textarea rows={5} className={inputClass} value={form.personality_prompt || ""} onChange={(e) => setForm({ ...form, personality_prompt: e.target.value })} />
        </Field>
        <Field label="Memory">
          <textarea rows={6} className={inputClass} value={form.memory || ""} onChange={(e) => setForm({ ...form, memory: e.target.value })} />
        </Field>
        <Field label="Style and safety rules">
          <textarea rows={4} className={inputClass} value={form.style_rules || ""} onChange={(e) => setForm({ ...form, style_rules: e.target.value })} />
        </Field>

        <div className="flex justify-end">
          <button type="button" onClick={save} disabled={saving} className="h-10 px-4 rounded bg-[hsl(var(--primary))] text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </section>
    </div>
  );
}

function Field({ label = "", children } = {}) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function ToggleCard({ label = "", checked, onChange } = {}) {
  return (
    <label className="surface p-4 flex items-center justify-between gap-3 cursor-pointer">
      <span className="text-sm font-medium">{label}</span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} />
    </label>
  );
}
